import { useEffect } from "react";

/**
 * Global keyboard navigation detection hook.
 *
 * This hook detects whether the user is navigating via keyboard or mouse
 * and sets a data attribute on document.body accordingly.
 *
 * When keyboard navigation is detected (Tab, Arrow keys, or Backspace):
 * - Sets `data-keyboard-nav="true"` on document.body
 *
 * When mouse navigation is detected (click):
 * - Removes the `data-keyboard-nav` attribute
 *
 * This enables CSS to show focus outlines only for keyboard users:
 * ```css
 * [data-keyboard-nav="true"] *:focus {
 *   outline: 2px solid var(--brand-primary-600);
 *   outline-offset: 2px;
 * }
 * ```
 *
 * @example
 * // In App.tsx or root component
 * function App() {
 *   useKeyboardNavigation();
 *   return <div>...</div>;
 * }
 */
export function useKeyboardNavigation(): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Tab" ||
        e.key === "Backspace" ||
        e.key.startsWith("Arrow")
      ) {
        document.body.setAttribute("data-keyboard-nav", "true");
      }
    };

    const handleMouseDown = () => {
      document.body.removeAttribute("data-keyboard-nav");
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);
}

export default useKeyboardNavigation;
